import { useState } from 'react';
import PropTypes from 'prop-types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmtValue(value, unit) {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'object') return '—';
  let str;
  if (typeof value === 'number') {
    str = Math.abs(value) >= 1000
      ? value.toLocaleString(undefined, { maximumSignificantDigits: 4 })
      : parseFloat(value.toPrecision(4)).toString();
  } else {
    str = String(value);
  }
  return unit ? `${str} ${unit}` : str;
}

// ---------------------------------------------------------------------------
// Param row
// ---------------------------------------------------------------------------

function ParamRow({ param, onDoubleClick }) {
  const isModified = param.modified;
  return (
    <tr
      className={onDoubleClick ? 'cursor-pointer hover:bg-stone-50' : ''}
      onDoubleClick={onDoubleClick}
      title={onDoubleClick ? 'Double-click to edit' : undefined}
    >
      <td className="py-1.5 px-3 text-sm text-slate-700">
        {param.label}
        {isModified && <span className="ml-1 text-slate-400 text-xs">·</span>}
      </td>
      <td className={`py-1.5 px-3 text-sm font-mono tabular-nums ${isModified ? 'text-slate-900 font-medium' : 'text-slate-500'}`}>
        {fmtValue(param.value, param.unit)}
      </td>
      <td className="py-1.5 px-3 text-sm text-slate-300 font-mono tabular-nums">
        {isModified ? fmtValue(param.default_value, param.unit) : ''}
      </td>
    </tr>
  );
}

ParamRow.propTypes = {
  param: PropTypes.shape({
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.any,
    unit: PropTypes.string,
    default_value: PropTypes.any,
    modified: PropTypes.bool,
  }).isRequired,
  onDoubleClick: PropTypes.func,
};

// ---------------------------------------------------------------------------
// Node section (collapsible, params grouped by category)
// ---------------------------------------------------------------------------

function groupByCategory(params) {
  const groups = new Map(); // category → [param]
  for (const p of params) {
    const cat = p.category ?? 'Other';
    if (!groups.has(cat)) groups.set(cat, []);
    groups.get(cat).push(p);
  }
  return groups;
}

function NodeSection({ node, onEdit, onEditParam }) {
  const [open, setOpen] = useState(false);
  const modifiedCount = node.params.filter(p => p.modified).length;
  const categoryGroups = groupByCategory(node.params);
  const showCategoryHeaders = categoryGroups.size > 1;

  return (
    <div className="border border-stone-200 rounded-md overflow-hidden mb-2">
      <div className="flex items-center bg-stone-50 hover:bg-stone-100 transition-colors">
        <button
          className="flex-1 flex items-center justify-between px-3 py-2 text-left"
          onClick={() => setOpen(o => !o)}
        >
          <span className="text-sm font-medium text-slate-700">{node.node_label}</span>
          <div className="flex items-center gap-2 shrink-0 text-xs text-slate-400">
            {modifiedCount > 0 && <span>{modifiedCount} modified</span>}
            <span>{node.params.length} key parameters</span>
            <svg width="10" height="10" viewBox="0 0 10 10" className={`shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}>
              <path d="M2 3.5 L5 7 L8 3.5" fill="none" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </button>
        {onEdit && (
          <button
            className="px-3 py-2 text-xs text-slate-400 hover:text-slate-700 shrink-0 transition-colors"
            onClick={() => onEdit(node.node_id)}
            title="Open the full parameter editor for this node"
          >
            Edit / All Parameters
          </button>
        )}
      </div>
      {open && (
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-stone-100">
                <th className="py-1.5 px-3 text-xs font-medium text-slate-400 bg-white">Parameter</th>
                <th className="py-1.5 px-3 text-xs font-medium text-slate-400 bg-white">Value</th>
                <th className="py-1.5 px-3 text-xs font-medium text-slate-400 bg-white">Default</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-50">
              {[...categoryGroups.entries()].map(([cat, params]) => (
                <>
                  {showCategoryHeaders && (
                    <tr key={`cat-${cat}`} className="bg-stone-50">
                      <td colSpan={3} className="px-3 pt-2 pb-1 text-xs font-semibold text-slate-400 uppercase tracking-wide">
                        {cat}
                      </td>
                    </tr>
                  )}
                  {params.map(p => (
                    <ParamRow
                      key={p.name}
                      param={p}
                      onDoubleClick={onEditParam ? () => onEditParam(node.node_id, p.name) : undefined}
                    />
                  ))}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

NodeSection.propTypes = {
  node: PropTypes.shape({
    node_id: PropTypes.string,
    node_label: PropTypes.string.isRequired,
    params: PropTypes.array.isRequired,
  }).isRequired,
  onEdit: PropTypes.func,
  onEditParam: PropTypes.func,
};

// ---------------------------------------------------------------------------
// Summary bar
// ---------------------------------------------------------------------------

function SummaryBar({ summary, builderUrl }) {
  if (!summary) return null;
  const { total_active_params, total_modified } = summary;
  const parts = [`${total_active_params} active parameters`];
  if (total_modified > 0) parts.push(`${total_modified} modified from defaults`);
  return (
    <p className="text-xs text-slate-400 mb-4">
      {parts.join(' · ')}
      {builderUrl && (
        <>
          {' · '}
          <a
            href={builderUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            Open project in builder view ↗
          </a>
        </>
      )}
    </p>
  );
}

SummaryBar.propTypes = {
  summary: PropTypes.shape({
    total_active_params: PropTypes.number,
    total_modified: PropTypes.number,
    total_at_default: PropTypes.number,
  }),
  builderUrl: PropTypes.string,
};

// ---------------------------------------------------------------------------
// Main card
// ---------------------------------------------------------------------------

/**
 * Renders get_study_assumptions tool data.
 * Used in the workspace Assumptions tab.
 *
 * onEditNode(nodeId) — called when the user clicks "Edit" on a node section.
 * collapsed — when true, hides the node-level detail (summary + node sections).
 * onToggle  — called when the user clicks the pathway header to toggle collapse.
 */
export default function AssumptionsCard({ data, onEditNode, onEditParam, topologySlot, collapsed, onToggle, builderUrl }) {
  if (!data) return null;

  const { pathway_name, active_by_node, summary } = data;

  if (!active_by_node || active_by_node.length === 0) {
    return (
      <div className="p-4 text-sm text-slate-400">No active assumptions found.</div>
    );
  }

  return (
    <div className="p-5">
      {pathway_name && (
        <button
          className="flex items-center gap-1.5 text-sm font-semibold text-slate-500 uppercase tracking-wide mb-1 hover:text-slate-700 select-none"
          onClick={onToggle}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" className={`shrink-0 transition-transform ${collapsed ? '' : 'rotate-90'}`}>
            <path d="M4.5 2.5 L8 6 L4.5 9.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          {pathway_name}
        </button>
      )}
      {!collapsed && (
        <>
          {topologySlot}
          <SummaryBar summary={summary} builderUrl={builderUrl} />
          <div>
            {active_by_node.map(node => (
              <NodeSection
                key={node.node_id || node.node_label}
                node={node}
                onEdit={onEditNode}
                onEditParam={onEditParam}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

AssumptionsCard.propTypes = {
  data: PropTypes.shape({
    pathway_name: PropTypes.string,
    active_by_node: PropTypes.array,
    summary: PropTypes.object,
  }),
  onEditNode: PropTypes.func,
  onEditParam: PropTypes.func,
  topologySlot: PropTypes.node,
  collapsed: PropTypes.bool,
  onToggle: PropTypes.func,
  builderUrl: PropTypes.string,
};
